/**
 * Chart Annotations Component
 * Renders annotation markers on charts to highlight significant events
 * Note: This is rendered as an overlay on top of the chart container
 */

import React from 'react';
import { useTheme } from '../../contexts/ThemeContext.jsx';

export default function ChartAnnotations({ annotations = [], chartData = [], xKey = 'month' }) {
  const { theme } = useTheme();

  if (!annotations || annotations.length === 0 || !chartData || chartData.length === 0) return null;

  // Find annotation positions in chart data
  const annotationElements = annotations
    .map((annotation) => {
      const index = chartData.findIndex(
        (item) => item[xKey] === annotation.month || 
                 (annotation.date && new Date(item[xKey]).getTime() === new Date(annotation.date).getTime())
      );
      if (index === -1) return null;
      return { ...annotation, index };
    })
    .filter(Boolean);

  if (annotationElements.length === 0) return null;

  return (
    <div style={styles.container}>
      {annotationElements.map((annotation, idx) => {
        // Calculate position: each bar takes up 1/chartData.length of the width
        // Account for margins (left: 20px, right: 30px from Recharts default)
        const barWidth = 100 / chartData.length;
        const leftPercent = (annotation.index + 0.5) * barWidth; // Center of bar
        
        return (
          <div
            key={idx}
            style={{
              ...styles.annotation,
              left: `${leftPercent}%`,
            }}
            title={annotation.label}
          >
            <div style={{
              ...styles.marker,
              backgroundColor: annotation.severity === 'high' 
                ? 'var(--color-status-error-base)'
                : annotation.severity === 'medium'
                ? 'var(--color-status-warning-base)'
                : 'var(--color-status-info-base)',
            }} />
            <div style={{
              ...styles.label,
              backgroundColor: theme === 'dark' ? 'var(--color-bg-secondary-dark)' : 'var(--color-bg-primary)',
              color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
              borderColor: theme === 'dark' ? 'var(--color-border-medium-dark)' : 'var(--color-border-medium)',
            }}>
              {annotation.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const styles = {
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
    zIndex: 10,
  },
  annotation: {
    position: 'absolute',
    top: '10px',
    transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'var(--spacing-1)',
  },
  marker: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.8)',
  },
  label: {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-semibold)',
    padding: 'var(--spacing-1) var(--spacing-2)',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--color-border-medium)',
    whiteSpace: 'nowrap',
    pointerEvents: 'auto',
    boxShadow: 'var(--shadow-sm)',
  },
};

